import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useModal } from '../../contexts/ModalContext'
import { useCampaign } from '../../contexts/CampaignContext'
import { useAuth } from '../../contexts/AuthContext'
import AdminControls from '../AdminControls'
import LocationForm from '../forms/LocationForm'
import MarkdownText from '../MarkdownText'
import DescriptionSections from '../DescriptionSections'
import SortSelect, { usePersistedSortMode } from '../SortSelect'
import ViewToggle, { usePersistedViewMode } from '../ViewToggle'
import EntityTable from '../EntityTable'
import { sortEntities } from '../../utils/entitySort'
import { parseSections, serializeSections } from '../../utils/descriptionSections'

function LocationsTab({ focusId, onConsumeFocus }) {
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingLocation, setEditingLocation] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [sortMode, setSortMode] = usePersistedSortMode('sort:locations')
  const [viewMode, setViewMode] = usePersistedViewMode('view:locations')
  const [selectedLocation, setSelectedLocation] = useState(null)
  const { canEdit, campaign, isModuleEnabled } = useCampaign()
  const { alert: showAlert, confirm } = useModal()
  const navigate = useNavigate()
  const { authEpoch } = useAuth()

  // authEpoch: see the identical comment in ItemsTab.jsx — refetches this
  // tab if it was already mounted (and its fetch already 401'd) when the
  // session expired, so signing back in via the re-auth modal actually
  // recovers the page instead of leaving it stuck.
  useEffect(() => {
    fetchLocations()
  }, [authEpoch])

  // Open a specific location when navigated to via a [[wiki-link]].
  useEffect(() => {
    if (!focusId || loading) return
    const target = locations.find((l) => l.id === focusId)
    if (target) {
      setSelectedLocation(target)
      onConsumeFocus?.()
    }
  }, [focusId, loading, locations])

  useEffect(() => {
    if (!selectedLocation) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedLocation(null)
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [selectedLocation])

  const fetchLocations = () => {
    // See ItemsTab.jsx's fetchItems for why: a non-OK response still has a
    // JSON body, so this must not hand it to setLocations() unguarded, and a
    // successful retry must clear any previous error.
    fetch('/api/locations')
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('Failed to load locations'))))
      .then(data => {
        setLocations(Array.isArray(data) ? data : [])
        setError(null)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }

  const handleEdit = (location) => {
    setEditingLocation(location)
  }

  const handleCreate = () => {
    setIsCreating(true)
  }

  const handleFormSubmit = async (formData, iconFile, deleteIcon) => {
    try {
      const isEdit = !!editingLocation
      const response = await fetch('/api/locations', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit ? { ...formData, id: editingLocation.id } : formData)
      })

      if (response.ok) {
        const result = await response.json()

        // Get the location ID (either from editing or from the newly created location)
        const locationId = isEdit ? editingLocation.id : result.id

        // Handle icon deletion
        if (deleteIcon) {
          const deleteResponse = await fetch('/api/locations/delete-icon', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ location_id: locationId })
          })

          if (!deleteResponse.ok) {
            console.error('Failed to delete icon')
          }
        }
        // Handle icon upload
        else if (iconFile) {
          const uploadFormData = new FormData()
          uploadFormData.append('icon', iconFile)
          uploadFormData.append('location_id', locationId)

          const uploadResponse = await fetch('/api/locations/upload-icon', {
            method: 'POST',
            body: uploadFormData
          })

          if (!uploadResponse.ok) {
            const error = await uploadResponse.json()
            showAlert({
              title: 'Warning',
              message: `Location saved but icon upload failed: ${error.error}`,
              type: 'error'
            })
          }
        }

        await fetchLocations()
        showAlert({
          title: isEdit ? 'Location Updated' : 'Location Created',
          message: `Location "${formData.name}" has been ${isEdit ? 'updated' : 'created'} successfully.`,
          type: 'success'
        })
        setEditingLocation(null)
        setIsCreating(false)
      } else {
        const error = await response.json()
        showAlert({
          title: 'Error',
          message: error.error || `Failed to ${isEdit ? 'update' : 'create'} location`,
          type: 'error'
        })
      }
    } catch (err) {
      showAlert({
        title: 'Error',
        message: `Failed to ${editingLocation ? 'update' : 'create'} location`,
        type: 'error'
      })
    }
  }

  const handleDelete = async (location) => {
    await confirm({
      title: 'Delete Location',
      message: `Are you sure you want to delete "${location.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/locations', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: location.id })
          })

          if (response.ok) {
            setLocations(locations.filter(l => l.id !== location.id))
            showAlert({
              title: 'Location Deleted',
              message: `"${location.name}" has been deleted.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to delete location',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to delete location',
            type: 'error'
          })
        }
      }
    })
  }

  const handleToggleVisibility = async (location) => {
    const action = location.is_visible ? 'hide' : 'reveal'
    await confirm({
      title: `${action === 'hide' ? 'Hide' : 'Reveal'} Location`,
      message: `${action === 'hide' ? 'Hide' : 'Reveal'} "${location.name}" ${action === 'hide' ? 'from' : 'to'} players?`,
      confirmText: action === 'hide' ? 'Hide' : 'Reveal',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/locations', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: location.id, is_visible: location.is_visible ? 0 : 1 })
          })

          if (response.ok) {
            setLocations(locations.map(l =>
              l.id === location.id ? { ...l, is_visible: location.is_visible ? 0 : 1 } : l
            ))
            showAlert({
              title: 'Visibility Updated',
              message: `"${location.name}" is now ${action === 'hide' ? 'hidden from' : 'visible to'} players.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to update visibility',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to update visibility',
            type: 'error'
          })
        }
      }
    })
  }

  const handleSetRevealed = async (index, revealed) => {
    const current = parseSections(selectedLocation.description_sections)
    const priorRevealed = current[index]?.revealed
    const next = current.map((s, i) => (i === index ? { ...s, revealed } : s))
    const payload = serializeSections(next)

    setSelectedLocation((prev) => ({ ...prev, description_sections: payload }))
    setLocations((prev) => prev.map((l) => (l.id === selectedLocation.id ? { ...l, description_sections: payload } : l)))

    try {
      const res = await fetch('/api/locations', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedLocation.id, description_sections: payload }),
      })
      if (!res.ok) throw new Error('Failed to update reveal')
    } catch (err) {
      // Revert only this index, re-parsed from whatever state is current at
      // revert time (not the stale `current` snapshot from above) — a second
      // flip of a different index may have already landed optimistically
      // while this request was in flight, and restoring the whole pre-flip
      // array would silently discard it.
      const revertOnlyThisIndex = (sections) =>
        serializeSections(
          parseSections(sections).map((s, i) => (i === index ? { ...s, revealed: priorRevealed } : s))
        )
      setSelectedLocation((prev) => ({ ...prev, description_sections: revertOnlyThisIndex(prev.description_sections) }))
      setLocations((prev) =>
        prev.map((l) =>
          l.id === selectedLocation.id ? { ...l, description_sections: revertOnlyThisIndex(l.description_sections) } : l
        )
      )
      showAlert({ title: 'Error', message: err.message, type: 'error' })
    }
  }

  if (loading) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Locations</h1>
          <p className="page-subtitle">Explore the world of your campaign</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>Loading locations...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Locations</h1>
          <p className="page-subtitle">Explore the world of your campaign</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: '#e74c3c' }}>Error loading locations: {error}</p>
        </div>
      </div>
    )
  }

  if (locations.length === 0) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>Locations</h1>
          <p className="page-subtitle">Explore the world of your campaign</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>🗺️</div>
          <h3 style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No Locations Discovered</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: canEdit ? '1.5rem' : 0 }}>
            {canEdit ? 'Add your first location to get this campaign started.' : 'Locations will appear here as you explore.'}
          </p>
          {canEdit && (
            <button
              onClick={handleCreate}
              style={{
                background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
                color: '#ffffff',
                border: '1px solid #27ae60',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
              }}
            >
              + Add New Location
            </button>
          )}
        </div>

        <LocationForm
          isOpen={isCreating || !!editingLocation}
          onClose={() => {
            setIsCreating(false)
            setEditingLocation(null)
          }}
          location={editingLocation}
          onSubmit={handleFormSubmit}
        />
      </div>
    )
  }

  // Filter and sort locations
  const trimmedFilter = filterText.trim()
  const isSearching = trimmedFilter.length > 0

  // The old comparator also pushed hidden-from-players rows to the end, but
  // that branch was unreachable: the filter above already drops every hidden
  // row whenever !canEdit, so both sides were always false.
  const filteredLocations = sortEntities(
    locations.filter(location => {
      // Players (and admins previewing as a player) never see hidden locations at all.
      if (!canEdit && !location.is_visible) return false
      if (!isSearching) return true
      const searchTerm = trimmedFilter.toLowerCase()
      const name = location.name?.toLowerCase() || ''
      const type = location.location_type?.toLowerCase() || ''
      const desc = location.description?.toLowerCase() || ''
      const fullDesc = location.full_description?.toLowerCase() || ''
      return name.includes(searchTerm) || type.includes(searchTerm) || desc.includes(searchTerm) || fullDesc.includes(searchTerm)
    }),
    sortMode
  )

  const locationColumns = [
    {
      key: 'icon',
      header: '',
      role: 'lead',
      render: (location) => {
        const isHidden = !location.is_visible && !canEdit
        return (
          <div className={`et-thumb is-square${isHidden ? ' is-unknown' : ''}`}>
            {isHidden ? '❓' : (
              location.icon_url
                ? <img src={location.icon_url} alt={location.name} />
                : '📍'
            )}
          </div>
        )
      },
    },
    {
      key: 'name',
      header: 'Location',
      role: 'name',
      render: (location) => {
        const isHidden = !location.is_visible && !canEdit
        return (
          <div className="et-name">
            {isHidden ? 'Unknown Location' : location.name}
            {canEdit && !location.is_visible && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', background: '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '3px' }}>Hidden</span>
            )}
          </div>
        )
      },
    },
    {
      key: 'type',
      header: 'Type',
      role: 'meta',
      // location_type is free text with datalist suggestions rather than a
      // true enum, so this column is bounded in practice but not by schema —
      // it takes the width of the longest value actually present.
      render: (location) => {
        const isHidden = !location.is_visible && !canEdit
        if (isHidden) return null
        return (
          <span style={{ background: 'var(--accent-teal)', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, display: 'inline-block' }}>
            {location.location_type}
          </span>
        )
      },
    },
    {
      key: 'description',
      header: 'Description',
      role: 'desc',
      render: (location) => {
        const isHidden = !location.is_visible && !canEdit
        return isHidden
          ? 'This location has not been discovered yet.'
          : <MarkdownText>{location.description}</MarkdownText>
      },
    },
    {
      key: 'map',
      header: 'On map',
      role: 'meta',
      // Something the card genuinely cannot show: whether the row has
      // map_x/map_y set. In card view you have to open the interactive map to
      // discover what's still unpinned.
      render: (location) => {
        const pinned = location.map_x !== null && location.map_x !== undefined
          && location.map_y !== null && location.map_y !== undefined
        return (
          <span className={`et-num${pinned ? '' : ' is-empty'}`}>
            {pinned ? '📍 Pinned' : '— Unpinned'}
          </span>
        )
      },
    },
    {
      key: 'actions',
      header: '',
      role: 'actions',
      render: (location) => canEdit && (
        <AdminControls
          item={location}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleVisibility={handleToggleVisibility}
          type="location"
        />
      ),
    },
  ]

  return (
    <div className="tab-pane">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div className="page-header" style={{ marginBottom: 0, flexShrink: 0 }}>
          <h1>Locations</h1>
          <p className="page-subtitle">Explore the world of your campaign</p>
        </div>
        <input
          type="text"
          placeholder="🔍 Filter locations by name, type, or description..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{
            flex: '1 1 240px',
            minWidth: '200px',
            padding: '0.75rem 1rem',
            background: 'var(--accent-deep)',
            border: '1px solid var(--accent-teal)',
            borderRadius: '8px',
            color: 'var(--text-primary)',
            fontSize: '0.95rem',
            fontFamily: 'inherit'
          }}
        />
        <SortSelect id="locations-sort" value={sortMode} onChange={setSortMode} />
        <ViewToggle value={viewMode} onChange={setViewMode} />
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {isModuleEnabled('map') && (
            <button
              onClick={() => navigate(`/c/${campaign.slug}/map`)}
              style={{
                background: 'linear-gradient(135deg, #1e4d5c 0%, #2a5566 100%)',
                color: 'var(--text-primary)',
                border: '2px solid var(--accent-teal)',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 12px rgba(30, 77, 92, 0.5)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(30, 77, 92, 0.6)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 77, 92, 0.5)'
              }}
            >
              <span>🗺️</span> Interactive Map
            </button>
          )}
          {canEdit && (
            <button
              onClick={handleCreate}
              style={{
                background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
                color: '#ffffff',
                border: '1px solid #27ae60',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
              }}
            >
              + Add New Location
            </button>
          )}
        </div>
      </div>

      {viewMode === 'table' ? (
        <EntityTable
          rows={filteredLocations}
          columns={locationColumns}
          label="Locations"
          onRowClick={setSelectedLocation}
          isDimmed={(location) => !location.is_visible}
          emptyMessage={isSearching ? 'No locations match that filter.' : 'No locations yet.'}
        />
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.5rem' }}>
        {filteredLocations.map(location => {
          const isHidden = !location.is_visible && !canEdit

          return (
            <div
              key={location.id}
              onClick={() => setSelectedLocation(location)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedLocation(location)
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                background: 'var(--card-bg)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                display: 'flex',
                flexDirection: 'column',
                opacity: location.is_visible ? 1 : 0.6,
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 8px 18px rgba(0, 0, 0, 0.25)'
                e.currentTarget.style.borderColor = 'var(--accent-teal)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = 'none'
                e.currentTarget.style.borderColor = 'var(--border-color)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.75rem' }}>
                {/* Icon/Image — omitted entirely when nothing has been uploaded */}
                {(isHidden || location.icon_url) && (
                  <div style={{ width: '70px', height: '70px', borderRadius: '8px', background: isHidden ? '#6b7f91' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: 'var(--text-primary)', flexShrink: 0, overflow: 'hidden', border: isHidden ? '1px solid var(--border-color)' : '2px solid var(--accent-teal)' }}>
                    {isHidden ? (
                      '❓'
                    ) : (
                      <img
                        src={location.icon_url}
                        alt={location.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0, marginBottom: '0.35rem', wordWrap: 'break-word' }}>
                    {isHidden ? 'Unknown Location' : location.name}
                    {canEdit && !location.is_visible && <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', background: '#6b7f91', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '3px' }}>Hidden</span>}
                  </h3>
                  {!isHidden && (
                    <span style={{ background: 'var(--accent-teal)', color: 'var(--text-primary)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, display: 'inline-block' }}>
                      {location.location_type}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: '0.9rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {isHidden ? 'This location has not been discovered yet.' : <MarkdownText>{location.description}</MarkdownText>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '0.5rem', marginTop: 'auto', paddingTop: '0.75rem' }}>
                {!isHidden ? <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontStyle: 'italic' }}>(click to see more)</span> : <span />}
                {canEdit && (
                  <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
                    <AdminControls
                      item={location}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleVisibility={handleToggleVisibility}
                      type="location"
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      )}

      {selectedLocation && (() => {
        const isHidden = !selectedLocation.is_visible && !canEdit
        const hasImage = !isHidden && selectedLocation.icon_url
        const fullDescription = selectedLocation.full_description?.trim()
          ? selectedLocation.full_description
          : selectedLocation.description

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(5, 10, 18, 0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '1rem'
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-dark) 100%)',
                border: '2px solid var(--accent-teal)',
                borderRadius: '12px',
                maxWidth: '980px',
                width: '100%',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.6)',
                position: 'relative',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                // See SessionsTab for why padding moved onto the children.
                overflow: 'hidden'
              }}
            >
              {/* Non-scrolling header row — an absolutely-positioned Close
                  button would sit on top of the scroll wrapper's scrollbar. */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 1rem 0.5rem', flexShrink: 0 }}>
                <button
                  onClick={() => setSelectedLocation(null)}
                  style={{
                    background: 'linear-gradient(135deg, #3a4550 0%, #2d363f 100%)',
                    color: '#ffffff',
                    border: '1px solid #5a6570',
                    borderRadius: '8px',
                    padding: '0.6rem 1.4rem',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                  aria-label="Close location details"
                >
                  Close
                </button>
              </div>

              <div style={{ overflowY: 'auto', minHeight: 0, padding: '0 1rem 2rem 2rem', scrollbarGutter: 'stable' }}>
              <div className="detail-float">
                {(isHidden || hasImage) && (
                <div
                  className="detail-float-media"
                  style={{
                    width: '420px',
                    height: '420px',
                    maxWidth: '80vw',
                    maxHeight: '60vh',
                    borderRadius: '12px',
                    background: isHidden ? '#6b7f91' : (hasImage ? 'transparent' : 'var(--accent-deep)'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '7rem',
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    border: hasImage ? '2px solid var(--accent-teal)' : '1px solid var(--border-color)',
                    flexShrink: 0
                  }}
                >
                  {isHidden ? (
                    '❓'
                  ) : (
                    <img
                      src={selectedLocation.icon_url}
                      alt={selectedLocation.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  )}
                </div>
                )}

                <div>
                  <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
                    {isHidden ? 'Unknown Location' : selectedLocation.name}
                  </h2>
                  {!isHidden && (
                    <div style={{ background: 'var(--accent-teal)', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, display: 'inline-block', marginBottom: '1rem' }}>
                      {selectedLocation.location_type}
                    </div>
                  )}
                  <div style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '1rem' }}>
                    {isHidden ? 'This location has not been discovered yet.' : <MarkdownText style={{ whiteSpace: 'pre-line' }}>{fullDescription}</MarkdownText>}
                  </div>
                  {!isHidden && (
                    <DescriptionSections
                      sections={selectedLocation.description_sections}
                      editable={canEdit}
                      onSetRevealed={handleSetRevealed}
                    />
                  )}
                </div>
              </div>
              </div>
            </div>
          </div>
        )
      })()}

      <LocationForm
        isOpen={isCreating || !!editingLocation}
        onClose={() => {
          setIsCreating(false)
          setEditingLocation(null)
        }}
        location={editingLocation}
        onSubmit={handleFormSubmit}
      />
    </div>
  )
}

export default LocationsTab
